import { useState, useCallback, useEffect } from 'react';
import type { PaymentOption } from '../types';
import {
  paymentRepository,
  type PaymentMethodCode,
  isPaymentInfo,
} from '../data/paymentRepository';
import { resolvePaymentNavigatePath } from '../utils/paymentNavigate';

/**
 * usePaymentMethod のエラー
 *
 * `code` には API レスポンス `errors[0].errorCode` を入れる（あれば）。
 * 画面側で固有の挙動（端末重複エラー時に専用文言＋「元の支払選択に戻る」ボタンを出す等）
 * を切り替えるために利用する。
 */
export interface PaymentMethodError {
  message: string;
  code?: string;
}

/** API側 PROCESSED_SYSTEM_ERROR コード。ドクター端末で進行中のため操作不可。 */
export const ERROR_CODE_PROCESSED_SYSTEM = 'E4009201';

/**
 * 支払方法選択画面の mode
 *
 * - `order`: 注文サイン前の事前選択（次画面: 注文サイン画面）
 * - `change`: 支払方法の変更（次画面: 新メソッドに応じた支払情報入力画面）
 */
export type PaymentMethodMode = 'order' | 'change';

/**
 * usePaymentMethod フックの戻り値
 */
interface UsePaymentMethodReturn {
  /** 選択中の支払方法（GET で復元されるか、ユーザー選択） */
  selectedMethod: PaymentOption | null;
  /** GET /payment でデータ取得中か */
  isLoading: boolean;
  /** POST /payment/set 送信中か */
  isSubmitting: boolean;
  /** 「次へ」ボタンを押下可能か */
  canProceed: boolean;
  /** エラー（API側 errorCode を含む） */
  error: PaymentMethodError | null;
  /** ユーザーが選択肢をタップしたときに呼ぶ */
  selectMethod: (method: PaymentOption) => void;
  /** 「次へ」押下時に呼ぶ。POST /payment/set → 遷移先パスを返す */
  submit: (inquiryId: string) => Promise<string | null>;
  /** 元の支払選択（GET /payment 取得時の値）に応じた遷移先パスを返す。元選択が無い場合は null */
  getOriginalPaymentPath: (inquiryId: string) => string | null;
  /** 画面の入力値・選択値・エラーを元の状態（GET 取得値）に戻す */
  resetToOriginal: () => void;
  /**
   * 利用可能な支払方法のコードリスト。
   * API 取得前は空配列（全 disabled）、取得失敗時も空配列のまま。
   * ドクター側 usePaymentPage と同じ挙動。
   */
  availableMethods: PaymentMethodCode[];
}

/**
 * `PaymentOption` ↔ `PaymentMethodCode` 変換
 *
 * UI で扱う値（PaymentOption）と API でやり取りする値（PaymentMethodCode）は
 * 同じ文字列なので 1:1 マッピング。型を分けているのは、UI 側の状態としての
 * 「未選択 (null)」を表現しつつ、API 送信時には null を弾くため。
 */
const toCode = (option: PaymentOption | null): PaymentMethodCode | null => {
  if (option === 'credit') return 'credit';
  if (option === 'cash') return 'cash';
  if (option === 'postpaid') return 'postpaid';
  if (option === 'bill') return 'bill';
  return null;
};

const fromCode = (code: PaymentMethodCode | null): PaymentOption | null => {
  if (code === 'credit') return 'credit';
  if (code === 'cash') return 'cash';
  if (code === 'postpaid') return 'postpaid';
  if (code === 'bill') return 'bill';
  return null;
};

/**
 * 選択した支払方法と mode から、次画面のパスを決定する
 *
 * - mode=order: 常に注文サイン画面 `/inquiries/:id/sign`
 * - mode=change + credit: `/inquiries/:id/credit-card`
 * - mode=change + cash: `/inquiries/:id/cash-payment-confirm`
 * - mode=change + postpaid: `/inquiries/:id/postpaid-confirm`
 * - mode=change + bill: `/inquiries/:id/cash-payment-confirm`
 */
const getNextPath = (
  inquiryId: string,
  mode: PaymentMethodMode,
  method: PaymentOption,
): string => {
  if (mode === 'order') {
    return `/inquiries/${inquiryId}/sign`;
  }
  // mode === 'change'
  switch (method) {
    case 'credit':
      return `/inquiries/${inquiryId}/credit-card`;
    case 'cash':
      return `/inquiries/${inquiryId}/cash-payment-confirm`;
    case 'postpaid':
      return `/inquiries/${inquiryId}/postpaid-confirm`;
    case 'bill':
      return `/inquiries/${inquiryId}/cash-payment-confirm`;
    default:
      return `/inquiries/${inquiryId}`;
  }
};

/**
 * 支払方法選択を管理するフック
 *
 * 画面マウント時に GET /payment を呼び、既存選択があれば復元する。
 * 「次へ」押下時は POST /payment/set し、mode に応じた次画面のパスを返す。
 *
 * @param mode 注文前選択 (`order`) or 支払方法変更 (`change`)
 * @param inquiryId 問い合わせ ID（GET 用）
 * @param token 認証トークン
 */
export const usePaymentMethod = (
  mode: PaymentMethodMode,
  inquiryId: string,
  token?: string,
): UsePaymentMethodReturn => {
  const [selectedMethod, setSelectedMethod] = useState<PaymentOption | null>(null);
  // GET /payment 取得時の支払選択を保持。「元の支払選択に戻る」(PPTX指示 0506 スライド11) で参照する。
  const [originalMethod, setOriginalMethod] = useState<PaymentOption | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<PaymentMethodError | null>(null);
  // 利用可能な支払方法のコードリスト。初期値は空配列 = 全 disabled。
  // 取得成功時のみ更新し、取得失敗時は空配列のまま（ドクター側 usePaymentPage 踏襲）。
  const [availableMethods, setAvailableMethods] = useState<PaymentMethodCode[]>([]);

  // マウント時に既存選択を GET で復元 + 利用可能な支払方法リストを並行取得
  useEffect(() => {
    let cancelled = false;
    if (!inquiryId) {
      setIsLoading(false);
      return;
    }
    (async () => {
      setIsLoading(true);
      // payment と availableMethods を並行取得（パフォーマンス上の最適化、ドクター usePaymentPage と同じ）
      const [paymentResult, methodsResult] = await Promise.all([
        paymentRepository.getPayment(inquiryId, token),
        paymentRepository.getAvailablePaymentMethods(inquiryId, token),
      ]);
      if (cancelled) return;
      if (paymentResult.success) {
        const data = paymentResult.data;
        if (data && isPaymentInfo(data)) {
          // 支払情報あり → 既存選択を復元、元の選択として保持
          const restored = fromCode(data.paymentMethodCode);
          setSelectedMethod(restored);
          setOriginalMethod(restored);
        }
        // estimateInfo / null の場合は未選択状態のまま
      } else {
        setError({
          message: paymentResult.error.message,
          code: paymentResult.error.errors?.[0]?.errorCode,
        });
      }
      // 取得成功時のみ更新。失敗時は初期値の空配列 = 全 disabled のまま（ドクター挙動踏襲）。
      if (methodsResult.success) {
        setAvailableMethods(methodsResult.data.paymentMethodCodes);
      }
      setIsLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [inquiryId, token]);

  const selectMethod = useCallback((method: PaymentOption) => {
    setSelectedMethod(method);
    setError(null);
  }, []);

  const submit = useCallback(
    async (id: string): Promise<string | null> => {
      if (!selectedMethod) return null;
      const code = toCode(selectedMethod);
      if (!code) {
        setError({ message: '対応していない支払方法です' });
        return null;
      }
      // 利用不可の支払方法は送信させない（ラジオボタン非活性の二重ガード、ドクター挙動踏襲）。
      if (!availableMethods.includes(code)) return null;

      setIsSubmitting(true);
      setError(null);

      // POST /payment/set は支払方法のみ受け取る仕様に変更されたため billingAddress は送らない。
      // 請求書払いの請求先情報は決済確定（createPayment）時に別途送信する。
      const result = await paymentRepository.setPaymentMethod(id, code, token);
      setIsSubmitting(false);

      if (!result.success) {
        setError({
          message: result.error.message,
          code: result.error.errors?.[0]?.errorCode,
        });
        return null;
      }

      return getNextPath(id, mode, selectedMethod);
    },
    [selectedMethod, mode, token, availableMethods],
  );

  /**
   * 元の支払選択（GET /payment 取得時の値）に応じた遷移先を返す。
   * PPTX指示 0506 スライド11「元の支払選択に応じた画面を表示」用。
   * 元の選択が無い場合は null（呼び出し側はフォールバック不要、ボタン非表示）。
   */
  const getOriginalPaymentPath = useCallback(
    (id: string): string | null => {
      if (!originalMethod) return null;
      const code = toCode(originalMethod);
      if (!code) return null;
      // isPaid=false, hasEstimateInfo=false: 元選択が決まっていれば必ず該当 path が返る
      return resolvePaymentNavigatePath(id, code, false, false);
    },
    [originalMethod],
  );

  /**
   * 画面上の選択・エラーを GET 取得時の状態に戻す。
   * PPTX指示 0506 スライド11「この画面の情報は全てリフレッシュ」用。
   * - 選択中の支払方法 → 元の支払選択
   * - エラー → クリア
   */
  const resetToOriginal = useCallback(() => {
    setSelectedMethod(originalMethod);
    setError(null);
  }, [originalMethod]);

  // 請求書払いの請求先情報は確認画面（CashPaymentConfirm）で入力する方式に統一したため、
  // この画面では支払方法の選択のみを検証する。
  // 利用可能な支払方法に含まれない選択は次へ進めない（ドクター usePaymentPage 踏襲）。
  const canProceed = (() => {
    if (selectedMethod === null || isLoading || isSubmitting) return false;
    const code = toCode(selectedMethod);
    if (!code || !availableMethods.includes(code)) return false;
    return true;
  })();

  return {
    selectedMethod,
    isLoading,
    isSubmitting,
    canProceed,
    error,
    selectMethod,
    submit,
    getOriginalPaymentPath,
    resetToOriginal,
    availableMethods,
  };
};
